import { type ChangeEvent, useMemo, useState } from "react";
import { Combobox } from "@headlessui/react";
import ChevronDown from "../Atomos/Icons/ChevronDown";
import { countryCodes } from "../../pages/ProfileEditMode/CountryCodes";
import { staffFieldRoot } from "./fieldStyles";

export interface IPhoneNumber {
  code: string;
  phone: string;
  basecode: string;
}

type CountryOption = {
  id: number;
  code: string;
  dial: string;
  countryName: string;
};

type StaffPhoneNumberProps = {
  className?: string;
  title?: string;
  Obj: IPhoneNumber;
  seter: (value: IPhoneNumber) => void;
  required?: boolean;
  error?: boolean;
  errormsj?: string;
  tooltip?: string;
  disabled?: boolean;
  getid?: boolean;
  type?: string;
};

const baseFieldClasses =
  "w-full rounded-md text-left text-sm transition-colors focus-within:outline-none";

const countryOptions: CountryOption[] = countryCodes.map((country, index) => ({
  id: index,
  code: country.code,
  dial: country.dial_code,
  countryName: country.name,
}));

const formatPhoneNumber = (value: string) => {
  const cleaned = value.replace(/\D/g, "").slice(0, 10);
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

  if (!match) {
    return cleaned;
  }
  return [match[1], match[2], match[3]].filter(Boolean).join(" ");
};

export const PhoneNumber = ({
  className = "",
  title = "Phone Number",
  Obj,
  seter,
  error = false,
  errormsj,
  tooltip,
  disabled = false,
  required = false,
}: StaffPhoneNumberProps): JSX.Element => {
  const [query, setQuery] = useState("");

  const selectedCountry = useMemo(
    () =>
      countryOptions.find(
        (option) => option.dial === Obj.code && option.code === Obj.basecode
      ) ?? null,
    [Obj.basecode, Obj.code]
  );

  const filteredCountries = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return countryOptions;
    }

    return countryOptions.filter((option) => {
      const optionText = `${option.code} ${option.dial}`.toLowerCase();
      return (
        optionText.includes(normalizedQuery) ||
        option.countryName.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [query]);

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    seter({ ...Obj, phone: formatPhoneNumber(event.target.value) });
  };

  const handleCountryChange = (option: CountryOption | null) => {
    if (!option) {
      seter({ ...Obj, code: "", basecode: "" });
      return;
    }

    seter({ ...Obj, code: option.dial, basecode: option.code });
  };

  return (
    <div className={`${staffFieldRoot} ${className}`}>
      <label title={tooltip} className="font-semibold text-gray-font">
        {title}
        {required && <span className="text-primary"> *</span>}
      </label>

      <div
        className={`${baseFieldClasses} ${
          disabled
            ? "cursor-not-allowed bg-gray-100 text-gray-400 outline outline-2 outline-gray-200"
            : error
              ? "bg-gray-25 text-primary outline outline-2 outline-primary"
              : "bg-gray-25 text-input-gray"
        } px-3 py-[11px]`}
      >
        <div className="flex items-center gap-3">
          <Combobox
            value={selectedCountry}
            onChange={handleCountryChange}
            disabled={disabled}
            nullable
          >
            {({ open }) => (
              <div className="relative border-r border-gray-200 pr-7">
                <Combobox.Input
                  displayValue={(option: CountryOption | null) =>
                    option ? `${option.code} ${option.dial}` : ""
                  }
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="(+)"
                  className={`!m-0 !block !min-w-0 !border-0 !outline-none !bg-transparent !p-0 !text-sm !font-normal !leading-5 text-gray-font placeholder:text-gray-400 focus:!outline-none focus:!ring-0 disabled:cursor-not-allowed disabled:text-gray-400`}
                />

                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center">
                  <span
                    className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""} ${disabled ? "text-gray-300" : "text-gray-font"}`}
                  >
                    <ChevronDown />
                  </span>
                </Combobox.Button>

                <Combobox.Options className="absolute left-0 top-12 z-30 max-h-56 w-64 overflow-y-auto rounded-md border border-gray-100 bg-white py-1 shadow-xl">
                  {filteredCountries.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No results found
                    </div>
                  )}

                  {filteredCountries.map((option) => (
                    <Combobox.Option
                      key={option.id}
                      value={option}
                      className={({ active }) =>
                        `cursor-pointer px-3 py-2 text-sm text-gray-font ${active ? "bg-gray-50" : "bg-white"}`
                      }
                    >
                      {`${option.code} ${option.dial}`}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              </div>
            )}
          </Combobox>

          <input
            name={title}
            disabled={disabled}
            type="text"
            placeholder="000 000 0000"
            value={Obj.phone}
            onChange={handlePhoneChange}
            className="!m-0 !block !w-full !min-w-[100px] !border-0 !outline-none !bg-transparent !p-0 !text-sm !font-normal !leading-5 text-gray-font placeholder:text-gray-400 focus:!outline-none focus:!ring-0 disabled:cursor-not-allowed disabled:text-gray-400"
          />
        </div>
      </div>

      {error && (
        <span className="text-sm font-medium text-primary">
          {errormsj ?? (required ? "This Phone Number is Required" : "")}
        </span>
      )}
    </div>
  );
};
